import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { BookOpen, Shield, GraduationCap, MapPin, Search, Calendar, ChevronRight, Bell } from 'lucide-react';

const PrincetonUniversity: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Princeton University";

    return (
        <div className="min-h-screen bg-white font-serif text-[#121212] selection:bg-[#E87722] selection:text-white">
            {/* Top Utility Bar */}
            <div className="bg-[#121212] text-white py-2 px-6 flex justify-between items-center text-xs font-sans tracking-wide">
                <div className="flex gap-4">
                    <a href="#" className="hover:text-[#E87722] transition-colors">Alumni</a>
                    <a href="#" className="hover:text-[#E87722] transition-colors">Faculty & Staff</a>
                    <a href="#" className="hover:text-[#E87722] transition-colors">Students</a>
                    <a href="#" className="hover:text-[#E87722] transition-colors">Parents</a>
                </div>
                <div className="flex gap-4 items-center">
                    <Search size={14} className="hover:text-[#E87722] cursor-pointer" />
                    <a href="#" className="hover:text-[#E87722] transition-colors font-bold">Give</a>
                </div>
            </div>

            {/* Main Navigation */}
            <nav className="border-b border-gray-200 py-6 px-6 sticky top-0 bg-white/95 backdrop-blur z-50">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
                    {/* Logo Section */}
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-14 bg-[#E87722] text-black clip-path-shield flex items-center justify-center relative">
                            <Shield size={28} fill="currentColor" className="text-black" />
                        </div>
                        <div className="flex flex-col">
                           <span className="font-bold text-3xl uppercase tracking-tighter leading-none">{schoolName}</span>
                        </div>
                    </div>

                    {/* Nav Links */}
                    <div className="flex gap-8 font-sans text-sm font-bold uppercase tracking-widest text-[#333]">
                        <a href="#" className="hover:text-[#E87722] transition-colors">Academics</a>
                        <a href="#" className="hover:text-[#E87722] transition-colors">Admission</a>
                        <a href="#" className="hover:text-[#E87722] transition-colors">Research</a>
                        <a href="#" className="hover:text-[#E87722] transition-colors">Life at {schoolName.split(' ')[0]}</a>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <header className="relative h-[600px] overflow-hidden">
                <img 
                    src="https://images.unsplash.com/photo-1563535973-228ee4e451b6?q=80&w=2070&auto=format&fit=crop" 
                    alt="Princeton Campus" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                
                <div className="absolute bottom-0 left-0 w-full p-12 md:p-24">
                    <div className="max-w-4xl">
                        <span className="bg-[#E87722] text-black font-sans text-xs font-bold px-3 py-1 uppercase tracking-widest mb-6 inline-block">
                            Service to Humanity
                        </span>
                        <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                            In the Nation's Service and the Service of Humanity.
                        </h1>
                        <p className="text-white/90 text-xl font-sans max-w-2xl leading-relaxed mb-8">
                            A vibrant community of scholarship and learning that stands in service to the nation and the world.
                        </p>
                        <Button className="bg-[#E87722] hover:bg-[#d66b1d] text-black font-sans font-bold uppercase tracking-widest px-8 h-12 rounded-none">
                            Explore the Mission
                        </Button>
                    </div>
                </div>
            </header>

            {/* News / Highlights Grid */}
            <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto">
                <div className="flex justify-between items-end mb-12 border-b border-gray-200 pb-4">
                    <h2 className="text-3xl font-bold text-[#121212]">University News</h2>
                    <a href="#" className="font-sans font-bold text-[#E87722] flex items-center gap-2 hover:underline">
                        More Stories <ChevronRight size={16} />
                    </a>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                     <div className="md:col-span-2 relative group cursor-pointer">
                         <div className="overflow-hidden mb-4">
                             <img src="https://images.unsplash.com/photo-1523580494863-6f3031224c94?q=80&w=2070&auto=format&fit=crop" className="w-full h-[400px] object-cover transition-transform duration-700 group-hover:scale-105" alt="Research" />
                         </div>
                         <div className="flex gap-4 mb-2">
                             <span className="font-sans text-xs font-bold uppercase text-[#E87722]">Research</span>
                             <span className="font-sans text-xs font-bold uppercase text-gray-400">Oct 12, 2026</span>
                         </div>
                         <h3 className="text-3xl font-bold leading-tight group-hover:text-[#E87722] transition-colors">
                             Breakthrough in Quantum Computing Architecture Promises New Era of Discovery
                         </h3>
                     </div>

                     <div className="space-y-12">
                         {[
                             { cat: "Arts", title: "Student Orchestra performs Mahler at Richardson Auditorium" },
                             { cat: "Science", title: "New plasma physics laboratory opens for fusion research" },
                             { cat: "Community", title: "Annual 'Tigertones' concert supports local charities" }
                         ].map((item, i) => (
                             <div key={i} className="group cursor-pointer">
                                 <div className="w-12 h-1 bg-[#E87722] mb-4 group-hover:w-24 transition-all"></div>
                                 <span className="font-sans text-xs font-bold uppercase text-gray-500 mb-2 block">{item.cat}</span>
                                 <h3 className="text-xl font-bold leading-snug group-hover:text-[#E87722] transition-colors">
                                     {item.title}
                                 </h3>
                             </div>
                         ))}
                     </div>
                </div>
            </section>

            {/* Stats / Impact */}
            <section className="bg-[#121212] text-white py-24 px-6 md:px-12">
                <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-12 text-center">
                    {[
                        { val: "5:1", label: "Student-Faculty Ratio" },
                        { val: "98%", label: "Graduation Rate" },
                        { val: "No Loan", label: "Financial Aid Policy" },
                        { val: "300+", label: "Student Organizations" }
                    ].map((stat, i) => (
                        <div key={i} className="border-r border-gray-800 last:border-0 px-4">
                            <div className="text-4xl md:text-5xl font-bold text-[#E87722] mb-4">{stat.val}</div>
                            <div className="font-sans text-sm font-bold uppercase tracking-widest text-gray-400">{stat.label}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Event Calendar Short */}
            <section className="bg-[#f5f5f5] py-24 px-6 md:px-12">
                <div className="max-w-4xl mx-auto">
                    <h2 className="text-center text-3xl font-bold mb-16">Upcoming Events</h2>
                    <div className="space-y-4">
                        {[
                            { date: "OCT 24", title: "Public Lecture: The Future of Democracy", time: "4:30 PM • McCosh 50" },
                            { date: "OCT 25", title: "Art Museum: Late Thursdays", time: "5:00 PM • Art Museum" },
                            { date: "OCT 27", title: "Football vs. Harvard", time: "1:00 PM • Powers Field" },
                        ].map((event, i) => (
                            <div key={i} className="bg-white p-6 flex items-center gap-6 border-l-4 border-[#E87722] shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                                <div className="min-w-[80px] text-center font-sans">
                                    <div className="text-xs font-bold uppercase text-gray-500">{event.date.split(' ')[0]}</div>
                                    <div className="text-2xl font-bold text-[#121212]">{event.date.split(' ')[1]}</div>
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold mb-1 hover:text-[#E87722] transition-colors">{event.title}</h3>
                                    <p className="font-sans text-xs text-gray-500 uppercase tracking-wide">{event.time}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-[#121212] text-white pt-20 pb-12 px-6 border-t border-[#333]">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-12 mb-16">
                    <div className="col-span-1 md:col-span-2">
                        <div className="flex items-center gap-3 mb-6">
                            <Shield size={24} className="text-[#E87722]" fill="currentColor" />
                            <span className="font-bold text-xl uppercase tracking-wide">{schoolName}</span>
                        </div>
                        <p className="font-sans text-gray-400 text-sm leading-relaxed max-w-md">
                            Princeton, New Jersey 08544 USA <br/>
                            (609) 258-3000
                        </p>
                    </div>
                    <div>
                        <h4 className="font-sans text-xs font-bold uppercase tracking-widest text-[#E87722] mb-6">Resources</h4>
                        <ul className="space-y-3 font-sans text-sm text-gray-400">
                             <li><a href="#" className="hover:text-white">For Media</a></li>
                             <li><a href="#" className="hover:text-white">Careers</a></li>
                             <li><a href="#" className="hover:text-white">Emergency Info</a></li>
                             <li><a href="#" className="hover:text-white">Public Safety</a></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-sans text-xs font-bold uppercase tracking-widest text-[#E87722] mb-6">Connect</h4>
                        <div className="flex gap-4">
                            {/* Social Icons would go here */}
                             <div className="w-8 h-8 rounded-full bg-gray-800 hover:bg-[#E87722] transition-colors"></div>
                             <div className="w-8 h-8 rounded-full bg-gray-800 hover:bg-[#E87722] transition-colors"></div>
                             <div className="w-8 h-8 rounded-full bg-gray-800 hover:bg-[#E87722] transition-colors"></div>
                        </div>
                    </div>
                </div>
                <div className="max-w-7xl mx-auto border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center font-sans text-xs text-gray-500">
                     <p>© 2026 The Trustees of {schoolName}</p>
                     <div className="flex gap-6 mt-4 md:mt-0">
                         <a href="#" className="hover:text-white">Accessibility</a>
                         <a href="#" className="hover:text-white">Privacy</a>
                         <a href="#" className="hover:text-white">Copyright</a>
                     </div>
                </div>
            </footer>
        </div>
    );
};

export default PrincetonUniversity;
